import { Drawer, Flex, Icon, Portal, Spinner, Text } from '@chakra-ui/react'
import { Check, RefreshCw, X } from 'lucide-react'
import React, { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { returnFocusAfterPageChange } from '~/hooks/useRouteFocusAndTitle'
import { ApplicationRoutes } from '~/hooks/useRouting'

import { StatusAdvancedView } from './StatusAdvancedView'
import { StatusBasicView } from './StatusBasicView'

const RETURN_TO_KEY = 'statusReturnTo'

let statusOpener: HTMLElement | null = null

function getStatusReturnTo(): string {
  return sessionStorage.getItem(RETURN_TO_KEY) || '/'
}

/**
 * Records where closing the panel navigates back to and, when given, the
 * control that opened it so focus returns there.
 */
export function setStatusReturnTo(path: string, opener?: HTMLElement | null) {
  sessionStorage.setItem(RETURN_TO_KEY, path)
  statusOpener = opener ?? null
}

const TABS = [
  {
    key: 'basic',
    labelKey: 'statusPanel.tabBasic',
    fallback: 'Basic',
    route: ApplicationRoutes.STATUS,
  },
  {
    key: 'advanced',
    labelKey: 'statusPanel.tabAdvanced',
    fallback: 'Advanced',
    route: ApplicationRoutes.STATUS_ADVANCED,
  },
] as const

export const StatusPanel = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const announce = useAnnounce()
  const { pbx, isLoading, getUserSessionDependencies } = useUserState()
  const isAdvanced = location.pathname.startsWith(
    ApplicationRoutes.STATUS_ADVANCED,
  )
  const selectedTabIndex = isAdvanced ? 1 : 0
  const idPrefix = useId()
  const tabId = (index: number) => `${idPrefix}-tab-${TABS[index].key}`
  const panelId = (index: number) => `${idPrefix}-panel-${TABS[index].key}`
  const tabRefs = useRef<(HTMLElement | null)[]>([])

  // The refresh button drives its own visible state machine: the refetch
  // often resolves in well under a second and the data is usually unchanged,
  // so without explicit feedback the click looks like a no-op. We show the
  // spinner until the user-state load actually settles (isLoading false),
  // keeping it up for a minimum time so the action is perceptible, then a
  // brief "Updated" confirmation.
  const [refreshState, setRefreshState] = useState<
    'idle' | 'refreshing' | 'done'
  >('idle')
  const refreshStartedAt = useRef<number>(0)
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const MIN_SPINNER_MS = 600
  // Upper bound on the spinner: if the refetch never settles isLoading, force
  // the state machine out of "refreshing" so it can't spin indefinitely.
  const REFRESH_TIMEOUT_MS = 10_000

  const handleClose = () => {
    returnFocusAfterPageChange(statusOpener)
    statusOpener = null
    navigate(getStatusReturnTo())
  }

  const handleRefresh = () => {
    if (!pbx || refreshState === 'refreshing') return
    if (doneTimer.current) clearTimeout(doneTimer.current)
    refreshStartedAt.current = Date.now()
    setRefreshState('refreshing')
    getUserSessionDependencies(pbx)
  }

  const selectTab = (index: number) => {
    navigate(TABS[index].route)
    tabRefs.current[index]?.focus()
  }

  const handleTabKeyDown = (event: React.KeyboardEvent) => {
    const lastIndex = TABS.length - 1
    const nextIndex = {
      ArrowRight: selectedTabIndex === lastIndex ? 0 : selectedTabIndex + 1,
      ArrowLeft: selectedTabIndex === 0 ? lastIndex : selectedTabIndex - 1,
      Home: 0,
      End: lastIndex,
    }[event.key]
    if (nextIndex === undefined) return
    event.preventDefault()
    selectTab(nextIndex)
  }

  const isRefreshing = refreshState === 'refreshing'

  useEffect(() => {
    if (refreshState === 'refreshing')
      announce(t('statusPanel.refreshing', 'Refreshing…'))
    if (refreshState === 'done') announce(t('statusPanel.refreshed', 'Updated'))
  }, [refreshState, announce, t])

  // Transition refreshing -> done once the fetch settles, honoring the
  // minimum spinner time. Reflects real completion rather than a fixed timer.
  useEffect(() => {
    // Clear any pending done->idle timer first, so a rapid re-run of this
    // effect can't overwrite doneTimer.current and orphan the prior timeout.
    if (doneTimer.current) clearTimeout(doneTimer.current)
    if (refreshState !== 'refreshing') return

    // getUserSessionDependencies is fire-and-forget (returns void) and swallows
    // its own errors, so if the fetch fails without ever toggling isLoading the
    // spinner would spin forever. Bound it: while still loading, only arm a
    // safety timeout that forces the state machine back; the normal completion
    // path runs once isLoading clears.
    if (isLoading) {
      const safety = setTimeout(
        () => setRefreshState('done'),
        REFRESH_TIMEOUT_MS,
      )
      return () => clearTimeout(safety)
    }

    const elapsed = Date.now() - refreshStartedAt.current
    const remaining = Math.max(0, MIN_SPINNER_MS - elapsed)
    const id = setTimeout(() => {
      setRefreshState('done')
      doneTimer.current = setTimeout(() => setRefreshState('idle'), 1500)
    }, remaining)
    return () => clearTimeout(id)
  }, [isLoading, refreshState])

  useEffect(() => {
    return () => {
      if (doneTimer.current) clearTimeout(doneTimer.current)
    }
  }, [])

  return (
    <Drawer.Root
      open
      onOpenChange={(details) => {
        if (!details.open) handleClose()
      }}
      placement="bottom"
      size="full"
    >
      <Portal>
        <Drawer.Backdrop bg="bg.overlay" />
        <Drawer.Positioner>
          <Drawer.Content
            bg="bg.default"
            borderTopRadius="xl"
            display="flex"
            flexDirection="column"
            h="calc(100vh - 2rem)"
            mt="2rem"
          >
            <Flex
              align="center"
              justify="space-between"
              px={6}
              h="4.5rem"
              minH="4.5rem"
              borderBottom="2px solid"
              borderColor="border.default"
            >
              <Flex align="center" gap={6}>
                <Drawer.Title
                  fontSize="lg"
                  fontWeight="bold"
                  color="fg.default"
                >
                  {t('statusPanel.title', 'App status')}
                </Drawer.Title>
                <Flex
                  gap={1}
                  role="tablist"
                  aria-label={t('statusPanel.tabListLabel', 'App status views')}
                  onKeyDown={handleTabKeyDown}
                >
                  {TABS.map((tab, index) => {
                    const isSelected = index === selectedTabIndex
                    return (
                      <Flex
                        as="button"
                        {...({ type: 'button' } as { type: 'button' })}
                        ref={(element: HTMLElement | null) => {
                          tabRefs.current[index] = element
                        }}
                        role="tab"
                        id={tabId(index)}
                        aria-selected={isSelected}
                        aria-controls={panelId(index)}
                        tabIndex={isSelected ? 0 : -1}
                        key={tab.route}
                        px={3}
                        py={1.5}
                        borderRadius="md"
                        cursor="pointer"
                        bg={isSelected ? 'bg.muted' : undefined}
                        color={isSelected ? 'fg.default' : 'fg.muted'}
                        fontWeight={isSelected ? 'semibold' : 'medium'}
                        fontSize="sm"
                        _hover={{ bg: 'bg.muted' }}
                        onClick={() => navigate(tab.route)}
                      >
                        {t(tab.labelKey, tab.fallback)}
                      </Flex>
                    )
                  })}
                </Flex>
              </Flex>
              <Flex align="center" gap={2}>
                {refreshState !== 'idle' && (
                  <Text fontSize="xs" color="fg.muted">
                    {isRefreshing
                      ? t('statusPanel.refreshing', 'Refreshing…')
                      : t('statusPanel.refreshed', 'Updated')}
                  </Text>
                )}
                <Flex
                  as="button"
                  {...({ type: 'button' } as { type: 'button' })}
                  align="center"
                  justify="center"
                  boxSize={8}
                  borderRadius="md"
                  cursor={isRefreshing ? 'default' : 'pointer'}
                  _hover={{ bg: 'bg.muted' }}
                  onClick={handleRefresh}
                  title={t('statusPanel.refresh', 'Refresh')}
                  aria-label={t('statusPanel.refresh', 'Refresh')}
                  aria-disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <Spinner size="sm" color="fg.muted" />
                  ) : refreshState === 'done' ? (
                    <Icon boxSize={5} color="green.fg" aria-hidden>
                      <Check size={18} />
                    </Icon>
                  ) : (
                    <Icon boxSize={5} color="fg.muted" aria-hidden>
                      <RefreshCw size={18} />
                    </Icon>
                  )}
                </Flex>
                <Flex
                  as="button"
                  {...({ type: 'button' } as { type: 'button' })}
                  align="center"
                  justify="center"
                  boxSize={8}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ bg: 'bg.muted' }}
                  onClick={handleClose}
                  aria-label={t('statusPanel.close', 'Close')}
                >
                  <Icon boxSize={5} color="fg.muted" aria-hidden>
                    <X size={20} />
                  </Icon>
                </Flex>
              </Flex>
            </Flex>

            {TABS.map((tab, index) => {
              const isSelected = index === selectedTabIndex
              return (
                <Flex
                  key={tab.key}
                  role="tabpanel"
                  id={panelId(index)}
                  aria-labelledby={tabId(index)}
                  aria-busy={isSelected && isRefreshing}
                  hidden={!isSelected}
                  display={isSelected ? 'flex' : 'none'}
                  flex={1}
                  direction="column"
                  overflow="auto"
                  bg="bg.muted"
                  minH={0}
                >
                  {isSelected &&
                    (isAdvanced ? <StatusAdvancedView /> : <StatusBasicView />)}
                </Flex>
              )
            })}
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
